import { useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { PanelError, isOutcomeUnknown, toPanelError } from '@/console/api/errors'
import { backfillOrderProductSkuIds, lookupSkusByOwnerBarcode, lookupSkusByProductOption } from '@/console/api/skus'
import {
  OwnerBarcodeSkuLookupItem,
  OwnerBarcodeSkuLookupResponse,
  ProductOptionSkuLookupItem,
  ProductOptionSkuLookupResponse,
  SkuBackfillRequest,
  SkuBackfillResponse,
} from '@/console/api/types'
import { PanelState, toPanelState } from '@/console/state/panelState'
import { SKU_BACKFILL_RUNS_KEPT, SkuBackfillRun, loadSkuBackfillRuns, saveSkuBackfillRuns } from '@/console/state/skuBackfillRunStorage'
import { SKU_FORBIDDEN_MESSAGE, SKU_LOOKUP_AXIS } from '@/console/tabs/fopt/skuLookup'

export const SKU_LOOKUP_EMPTY_REASON = '조회 결과가 없습니다'

export type SkuLookupRequest =
  | { axis: typeof SKU_LOOKUP_AXIS.productOption; items: ProductOptionSkuLookupItem[] }
  | { axis: typeof SKU_LOOKUP_AXIS.ownerBarcode; items: OwnerBarcodeSkuLookupItem[] }

export type SkuLookupRun =
  | { axis: typeof SKU_LOOKUP_AXIS.productOption; response: ProductOptionSkuLookupResponse }
  | { axis: typeof SKU_LOOKUP_AXIS.ownerBarcode; response: OwnerBarcodeSkuLookupResponse }

export const devConsoleSkusQueryKeys = {
  all: ['console', 'dev-console-skus'] as const,
  lookup: (request: SkuLookupRequest) => [...devConsoleSkusQueryKeys.all, 'lookup', request] as const,
}

const IDLE_LOOKUP_KEY = [...devConsoleSkusQueryKeys.all, 'lookup', 'idle'] as const

type NoncedLookupRequest = { request: SkuLookupRequest; nonce: number }

const fetchSkuLookup = async (request: SkuLookupRequest): Promise<SkuLookupRun> => {
  if (request.axis === SKU_LOOKUP_AXIS.productOption) {
    return { axis: request.axis, response: await lookupSkusByProductOption({ items: request.items }) }
  }
  return { axis: request.axis, response: await lookupSkusByOwnerBarcode({ items: request.items }) }
}

/** 같은 조건으로 다시 조회해도 새 쿼리가 되게 queryKey 에 nonce 를 싣는다(선례: `useOrderTrace`). */
export const useSkuLookup = (): { state: PanelState<SkuLookupRun>; lookup: (request: SkuLookupRequest) => void } => {
  const [current, setCurrent] = useState<NoncedLookupRequest | null>(null)

  const query = useQuery<SkuLookupRun, unknown, SkuLookupRun, readonly unknown[]>({
    queryKey: current ? [...devConsoleSkusQueryKeys.lookup(current.request), current.nonce] : IDLE_LOOKUP_KEY,
    queryFn: () => fetchSkuLookup((current as NoncedLookupRequest).request),
    enabled: current !== null,
  })

  const lookup = (request: SkuLookupRequest): void => {
    setCurrent((previous) => ({ request, nonce: (previous?.nonce ?? 0) + 1 }))
  }

  const state = toPanelState(query, SKU_LOOKUP_EMPTY_REASON, (run) => run.response.results.length === 0)
  if (state.status === 'error') {
    return { state: { status: 'error', ...toPanelError(query.error, SKU_FORBIDDEN_MESSAGE) }, lookup }
  }
  return { state, lookup }
}

export type SkuBackfillController = {
  submitting: boolean
  error: PanelError | null
  /** 응답을 못 받은 제출 뒤 다음 성공 전까지 참. 서버가 이미 덮어썼다면 다시 덮어쓸 때 첫 previousSkuId 를 잃는다. */
  overwriteLocked: boolean
  /** 최근 제출이 앞에 온다. 새 제출이 앞선 결과를 덮지 않는다 — previousSkuId 를 잃지 않기 위해서다. */
  runs: SkuBackfillRun[]
  submit: (request: SkuBackfillRequest) => void
}

const toBackfillRun = (response: SkuBackfillResponse, request: SkuBackfillRequest): SkuBackfillRun => ({
  id: crypto.randomUUID(),
  submittedAt: new Date().toISOString(),
  overwrite: request.overwrite,
  results: response.results,
})

const useBackfillRuns = (): { runs: SkuBackfillRun[]; recordRun: (response: SkuBackfillResponse, request: SkuBackfillRequest) => void } => {
  const [runs, setRuns] = useState<SkuBackfillRun[]>(loadSkuBackfillRuns)

  const recordRun = (response: SkuBackfillResponse, request: SkuBackfillRequest): void => {
    const run = toBackfillRun(response, request)
    // 겹친 제출이 서로의 결과를 덮지 않게 렌더 시점 runs 대신 저장소를 다시 읽어 합친다.
    // 화면이 떠나 setState 가 버려져도 결과는 저장소에 남도록 여기서 바로 쓴다.
    saveSkuBackfillRuns([run, ...loadSkuBackfillRuns()].slice(0, SKU_BACKFILL_RUNS_KEPT))
    setRuns((previous) => [run, ...previous].slice(0, SKU_BACKFILL_RUNS_KEPT))
  }

  return { runs, recordRun }
}

const BACKFILL_OUTCOME_UNKNOWN_MESSAGE =
  '응답을 받지 못했어요. 서버가 이미 반영했을 수 있으니 overwrite 없이 다시 보내 현재 값부터 확인하세요. 그 전까지 overwrite 제출은 막아 둡니다.'

const toBackfillError = (error: unknown): PanelError =>
  isOutcomeUnknown(error) ? { kind: 'other', message: BACKFILL_OUTCOME_UNKNOWN_MESSAGE } : toPanelError(error, SKU_FORBIDDEN_MESSAGE)

/**
 * 쓰기라 재시도를 끈다 — 값은 망가지지 않지만 응답을 잃으면 previousSkuId 를 잃는다.
 * `inFlight` 는 리렌더 전에 들어온 두 번째 제출까지 막는다.
 */
export const useSkuBackfill = (): SkuBackfillController => {
  const { runs, recordRun } = useBackfillRuns()
  const inFlight = useRef(false)
  const [overwriteLocked, setOverwriteLocked] = useState(false)

  const mutation = useMutation<SkuBackfillResponse, unknown, SkuBackfillRequest>({
    retry: 0,
    mutationFn: backfillOrderProductSkuIds,
    onSuccess: (response, request) => {
      recordRun(response, request)
      setOverwriteLocked(false)
    },
    onError: (error) => {
      if (isOutcomeUnknown(error)) setOverwriteLocked(true)
    },
    onSettled: () => {
      inFlight.current = false
    },
  })

  const submit = (request: SkuBackfillRequest): void => {
    if (inFlight.current) return
    inFlight.current = true
    mutation.mutate(request)
  }

  return {
    submitting: mutation.isPending,
    error: mutation.isError ? toBackfillError(mutation.error) : null,
    overwriteLocked,
    runs,
    submit,
  }
}
